import { useT } from '@hana/i18n';
import '../i18n';
import type { ReactNode } from 'react';
import {
  IconArchive, IconBan, IconBookmark, IconClock, IconFlag, IconFolder, IconInbox, IconMail, IconMenu2, IconMessageCircle, IconPencil, IconPin, IconSend, IconTrash, IconTruckDelivery, IconFileText, IconCalendarTime,
} from '@tabler/icons-react';
import { QUOTA, boxCounts } from './view';
import s from './box.module.css';

// Cột hộp thư mở rộng (≥ 1800, board Mint-Box-1920): hộp chung · thư mục · thư mục thông minh · nhãn · dung lượng.

const ICO = { size: 16, stroke: 1.8, 'aria-hidden': true } as const;

function Item({ icon, label, n, on, aria, danger, warn }: { icon?: ReactNode; label: string; n?: number | string; on?: boolean; aria?: string; danger?: boolean; warn?: boolean }) {
  return (
    <button type="button" className={`${s.fi} ${on ? s.fiOn : ''}`} aria-label={aria} aria-current={on || undefined}>
      {icon}
      <span className={s.fiLb}>{label}</span>
      {n !== undefined && n !== 0 && <span className={`${s.fiN} ${warn ? s.fiWarn : ''}`}>{n}</span>}
      {danger && <span className={s.fiN} aria-hidden="true" />}
    </button>
  );
}

function Head({ children, add, addLabel, collapse }: { children: string; add?: string; addLabel?: string; collapse?: boolean }) {
  return (
    <div className={s.fh}>
      <span>{children}</span>
      <span className={s.fhR}>
        {add && <button type="button" className={s.fhBtn} aria-label={addLabel} style={{ color: 'var(--link)' }}>{add}</button>}
        {collapse && <button type="button" className={s.fhBtn} aria-label="Thu cột hộp thư (phím [)">«</button>}
      </span>
    </div>
  );
}

const DOT = { BOOKING: 'var(--d-blue)', URGENT_CASE: 'var(--err)', LICH_TAU: 'var(--d-amber)', DnD_PWC: 'var(--d-violet)', NOTICE: 'var(--d-gray)', CHUNG_TU: 'var(--d-green)' } as const;

export function FolderTree() {
  const t = useT();
  const c = boxCounts();
  const smart: ReadonlyArray<[string, number, string]> = [
    ['Chưa xử lý', c.smart.open, 'var(--d-blue)'],
    ['Chưa gán', c.smart.unassigned, 'var(--d-amber)'],
    ['Quá SLA', c.smart.late, 'var(--err)'],
    ['Của tôi (PIC)', c.smart.mine, 'var(--d-green)'],
    ['Theo dõi · Follow', c.smart.watch, 'var(--d-violet)'],
    ['Có job', c.smart.hasJob, 'var(--d-blue)'],
    ['Đã xử lý', c.smart.done, 'var(--d-gray)'],
  ];
  return (
    <>
      <Head add="+" addLabel="Thêm hộp chung" collapse>{t('box.mailbox')}</Head>
      <Item icon={<IconMenu2 {...ICO} />} label="Tất cả hộp thư" n={c.all} aria={`Tất cả hộp thư, ${c.all}`} />
      <Item icon={<IconInbox {...ICO} />} label="Hộp của tôi" n={c.mine} aria={`Hộp của tôi, ${c.mine}`} />
      <Item icon={<IconMail {...ICO} />} label="info@" n={`${c.infoUnassigned} chưa gán`} warn on aria={`info@, ${c.infoUnassigned} chưa gán`} />
      <Item icon={<IconMessageCircle {...ICO} />} label="support@" n={c.boxes.support} aria={`support@, ${c.boxes.support}`} />
      <Item icon={<IconCalendarTime {...ICO} />} label="booking@" aria="booking@" />
      <Item icon={<IconTruckDelivery {...ICO} />} label="trucking@" n={c.boxes.trucking} aria={`trucking@, ${c.boxes.trucking}`} />
      <Item icon={<IconFileText {...ICO} />} label="billing@" n={c.boxes.billing} aria={`billing@, ${c.boxes.billing}`} />
      <Head add="+" addLabel="Tạo thư mục">{t('box.folders')}</Head>
      <Item icon={<IconInbox {...ICO} />} label="Hộp đến" n={c.folders.inbox} on />
      <Item icon={<IconFlag {...ICO} />} label="Gắn cờ" />
      <Item icon={<IconPin {...ICO} />} label="Đã ghim" />
      <Item icon={<IconClock {...ICO} />} label="Follow Up" />
      <Item icon={<IconClock {...ICO} />} label="Tạm ẩn" n={c.folders.snz} />
      <Item icon={<IconPencil {...ICO} />} label="Nháp" />
      <Item icon={<IconSend {...ICO} />} label="Đã gửi" />
      <Item icon={<IconClock {...ICO} />} label="Hẹn gửi" />
      <Item icon={<IconArchive {...ICO} />} label="Lưu trữ" />
      <Item icon={<IconBan {...ICO} />} label="Thư rác" n={c.folders.junk} />
      <Item icon={<IconTrash {...ICO} />} label="Thùng rác" />
      {['Khách VIP', 'Claim 2026'].map((f) => (
        <div key={f} className={s.fcustom}>
          <Item icon={<IconFolder {...ICO} />} label={f} />
          <button type="button" className={s.fx} aria-label={`Xoá thư mục ${f}`}>×</button>
        </div>
      ))}
      <Head>{t('box.smartFolders')}</Head>
      {smart.map(([k, n, col]) => (
        <Item key={k} icon={<span className={s.fdot} style={{ background: col }} aria-hidden="true" />} label={k} n={n} aria={`${k} ${n}`} />
      ))}
      <Head add="+" addLabel="Tạo nhãn">{t('box.labels')}</Head>
      {c.labels.map(([k, n]) => (
        <Item key={k} icon={<IconBookmark size={14} stroke={1.8} aria-hidden="true" style={{ color: DOT[k as keyof typeof DOT] }} />} label={k} n={n} aria={`${k} ${n}`} />
      ))}
      <button type="button" className={s.quota}>
        <span className={s.quotaL}>{t('box.storage')}</span>
        <span className={s.quotaN}>{QUOTA.used}</span>
        <span className={s.quotaV}>{t('box.cleanup')}</span>
        <span className={s.quotaBar} aria-hidden="true"><span style={{ width: '31%' }} /></span>
      </button>
    </>
  );
}
